import React, { useEffect } from 'react';
import { Box, Grid, useTheme, IconButton } from '@ublend-npm/aulaui-core';
import widgetsLibrary, { HOME_SPACES_WIDGET_IDS } from './widgetsLibrary';
import SpaceWidgetSection from './widgets/spaceWidget';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWidgetPreferences } from '../../../actions/home/widgetPreferences';
import { WidgetPreference } from '@core/types/state';
import { THEME_SWITCHING } from '@core/constants/actions';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

const HOME_PAGE_HORIZONTAL_PADDING = '2rem';
const HOME_SPACES_WIDGET_ID_SET = new Set<string>(HOME_SPACES_WIDGET_IDS);

const Widgets = () => {
  const userPreferences: WidgetPreference[] | null = useSelector(
    (state: any) => state.home?.widgetPreferences
  );

  const widgetsToRender = widgetsLibrary
    .map((widget) => {
      const pref = (userPreferences || []).find(
        (p: any) => p.widgetId === widget.id
      );
      return {
        ...widget,
        isHidden: pref ? pref.isHidden : false,
      };
    })
    .filter((widget) => !widget.isHidden);

  const isWidgetVisible = (widgetId: string) =>
    widgetsToRender.some((widget) => widget.id === widgetId);

  const showSpacesSection = HOME_SPACES_WIDGET_IDS.some(isWidgetVisible);
  const otherWidgets = widgetsToRender.filter(
    (widget) => !HOME_SPACES_WIDGET_ID_SET.has(widget.id)
  );

  return (
    <>
      {showSpacesSection ? (
        <Box
          data-home-spaces-full-width
          sx={{
            width: '100vw',
            ml: 'calc(50% - 50vw)',
            boxSizing: 'border-box',
            mb: 3,
          }}
        >
          <Box
            sx={{
              px: HOME_PAGE_HORIZONTAL_PADDING,
              minWidth: 0,
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <SpaceWidgetSection
              showRoleToggle={isWidgetVisible('home-switchrole-widget')}
              showGreeting={isWidgetVisible('home-greeting-widget')}
              showSpaceCarousel={isWidgetVisible('home-space-widget')}
            />
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          px: HOME_PAGE_HORIZONTAL_PADDING,
          pb: '2rem',
          pt: showSpacesSection ? 0 : '1.5rem',
          boxSizing: 'border-box',
        }}
      >
        <Grid container spacing={3} flexWrap="wrap">
          {otherWidgets.map((widget) => (
            <Grid
              id={widget.id}
              size={widget.size}
              aria-label={widget?.ariaLabel}
              sx={{ minWidth: 0 }}
            >
              {widget.component}
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
};

const ThemeToggleFAB = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleToggle = () => {
    const nextTheme = isDarkMode ? 'light' : 'dark';
    localStorage.setItem('theme', nextTheme);
    dispatch({ type: THEME_SWITCHING, theme: nextTheme });
  };

  return (
    <IconButton
      onClick={handleToggle}
      aria-label="Toggle dark/light theme"
      sx={{
        position: 'fixed',
        bottom: '80px',
        right: '24px',
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        zIndex: 1000,
        backgroundColor: isDarkMode ? '#ffb74d' : '#1a237e',
        color: isDarkMode ? '#1a237e' : '#fff',
        boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.25)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          backgroundColor: isDarkMode ? '#ffa726' : '#0d1b60',
          transform: 'scale(1.1) rotate(360deg)',
          boxShadow: isDarkMode
            ? '0px 8px 24px rgba(255, 183, 77, 0.4)'
            : '0px 8px 24px rgba(26, 35, 126, 0.4)',
        },
        '&:active': {
          transform: 'scale(0.95)',
        },
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isDarkMode ? (
        <LightModeIcon
          sx={{ fontSize: '28px', transition: 'transform 0.4s' }}
        />
      ) : (
        <DarkModeIcon sx={{ fontSize: '28px', transition: 'transform 0.4s' }} />
      )}
    </IconButton>
  );
};

const HomePage = () => {
  const dispatch = useDispatch();

  const widgetPreferences: WidgetPreference[] | null = useSelector(
    (state: any) => state.home?.widgetPreferences
  );

  useEffect(() => {
    document.title = 'Home - Aula';
    if (!widgetPreferences) {
      dispatch(fetchWidgetPreferences() as any);
    }
  }, [dispatch, widgetPreferences]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        position: 'relative',
      }}
      gap="1.5rem"
      pt="1.5rem"
    >
      <Widgets />
      <ThemeToggleFAB />
    </Box>
  );
};

export default HomePage;
